import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { api, DEFAULT_WEIGHTS, FactorKey, ModelTuning, ModelWeights } from '../api/client';
import { useStore } from '../stores/store';

const FACTORS: { key: FactorKey; label: string; icon: string }[] = [
  { key: 'ranking', label: 'Ranking FIFA', icon: '🌍' },
  { key: 'form', label: 'Forma reciente', icon: '📈' },
  { key: 'goals', label: 'Goles (atq/def)', icon: '⚽' },
  { key: 'climate', label: 'Clima / adaptación', icon: '🌡️' },
  { key: 'h2h', label: 'Mano a mano', icon: '⚔️' },
];

const ITEM_H = 52;
const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

// Ventana independiente: "Ajuste del Oráculo". Rueda vertical para elegir el factor y un
// deslizador horizontal para su influencia (% normalizado; al subir uno, los demás se reparten).
export function OracleTuning() {
  const open = useStore((s) => s.oracleTuningOpen);
  const setOpen = useStore((s) => s.setOracleTuning);
  const predictAuto = useStore((s) => s.predictAuto);
  const predictWeights = useStore((s) => s.predictWeights);
  const setPredictWeights = useStore((s) => s.setPredictWeights);
  const resetPredictWeights = useStore((s) => s.resetPredictWeights);

  const [tuning, setTuning] = useState<ModelTuning | null>(null);
  useEffect(() => {
    if (open && !tuning) api.modelTuning().then(setTuning).catch(() => {});
  }, [open, tuning]);

  const [sel, setSel] = useState(0);
  const wheelRef = useRef<HTMLDivElement | null>(null);

  const autoW = tuning?.weights ?? null;
  const effective: ModelWeights = predictWeights ?? (predictAuto ? autoW ?? DEFAULT_WEIGHTS : DEFAULT_WEIGHTS);
  const total = useMemo(() => (Object.values(effective) as number[]).reduce((s, v) => s + v, 0) || 1, [effective]);
  const pct = (k: FactorKey) => Math.round((effective[k] / total) * 100);

  const selKey = FACTORS[sel].key;

  // Setear la INFLUENCIA (% del total) del factor elegido; el resto mantiene su proporción.
  const setShare = (sharePct: number) => {
    const others = (Object.keys(effective) as FactorKey[])
      .filter((k) => k !== selKey)
      .reduce((s, k) => s + effective[k], 0);
    const s = clamp(sharePct / 100, 0.01, 0.95);
    const wk = others > 0 ? (s * others) / (1 - s) : s;
    setPredictWeights({ ...effective, [selKey]: wk });
  };

  const onScroll = () => {
    const el = wheelRef.current;
    if (!el) return;
    const idx = clamp(Math.round(el.scrollTop / ITEM_H), 0, FACTORS.length - 1);
    if (idx !== sel) setSel(idx);
  };

  const tapItem = (i: number) => {
    setSel(i);
    wheelRef.current?.scrollTo({ top: i * ITEM_H, behavior: 'smooth' });
  };

  if (!open) return null;
  const auto = predictAuto && !predictWeights;

  return (
    <Overlay onClick={() => setOpen(false)}>
      <Card onClick={(e) => e.stopPropagation()}>
        <CloseX onClick={() => setOpen(false)} aria-label="Cerrar">✕</CloseX>
        <h3>🔮 Ajuste del Oráculo</h3>
        <p className="sub">Decidí cuánto pesa cada fuente en la predicción.</p>

        <Toggle>
          <span>Modo automático · el Oráculo aprende de los resultados</span>
          <input
            type="checkbox"
            checked={auto}
            onChange={(e) => (e.target.checked ? resetPredictWeights() : setPredictWeights({ ...effective }))}
          />
        </Toggle>

        <WheelWrap>
          <div className="frame" />
          <div className="wheel" ref={wheelRef} onScroll={onScroll}>
            {FACTORS.map((f, i) => (
              <button key={f.key} className={`item ${i === sel ? 'on' : ''}`} onClick={() => tapItem(i)}>
                <span className="ic">{f.icon}</span>
                <span className="lb">{f.label}</span>
                <span className="pc">{pct(f.key)}%</span>
              </button>
            ))}
          </div>
          <div className="hint">↑ deslizá para elegir la fuente ↓</div>
        </WheelWrap>

        <Influence>
          <div className="t">
            Influencia de <b>{FACTORS[sel].label}</b>
            <span className="v">{pct(selKey)}%</span>
          </div>
          <input
            type="range"
            min={1}
            max={80}
            value={pct(selKey)}
            onChange={(e) => setShare(Number(e.target.value))}
          />
          <div className="ends"><span>poco</span><span>← influencia →</span><span>mucho</span></div>
        </Influence>

        {tuning && tuning.sample > 0 && (
          <p className="acc">
            Calibrado con {tuning.sample} partidos · acierto{' '}
            {FACTORS.filter((f) => tuning.accuracy[f.key] != null)
              .map((f) => `${f.icon}${Math.round((tuning.accuracy[f.key] as number) * 100)}%`)
              .join(' ')}
          </p>
        )}

        <button className="reset" onClick={resetPredictWeights} disabled={auto}>
          Devolverle el poder al Oráculo (automático)
        </button>
      </Card>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(2, 6, 12, 0.7);
  backdrop-filter: blur(3px);
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 380px;
  max-height: 92vh;
  overflow-y: auto;
  background: linear-gradient(165deg, #1a1340, ${({ theme }) => theme.colors.surface} 65%);
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 20px 18px 18px;
  color: ${({ theme }) => theme.colors.text};
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.55);

  h3 { margin: 0 0 4px; font-size: 1.25rem; }
  .sub { margin: 0 0 14px; font-size: 0.86rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .acc { margin: 12px 0 0; font-size: 0.75rem; text-align: center; color: ${({ theme }) => theme.colors.textMuted}; }
  .reset {
    width: 100%;
    margin-top: 14px;
    background: none;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text};
    border-radius: 999px;
    padding: 11px;
    font-weight: 700;
  }
  .reset:disabled { opacity: 0.5; }
`;

const CloseX = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  color: #fff;
  border-radius: 999px;
  width: 34px;
  height: 34px;
  font-size: 1rem;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.86rem;
  font-weight: 600;
  margin-bottom: 14px;
  input { width: 22px; height: 22px; flex-shrink: 0; }
`;

const WheelWrap = styled.div`
  position: relative;
  .frame {
    position: absolute;
    left: 0;
    right: 0;
    top: ${ITEM_H}px;
    height: ${ITEM_H}px;
    border-top: 1px solid ${({ theme }) => theme.colors.accent};
    border-bottom: 1px solid ${({ theme }) => theme.colors.accent};
    background: rgba(26, 168, 106, 0.08);
    pointer-events: none;
    border-radius: 8px;
  }
  .wheel {
    height: ${ITEM_H * 3}px;
    overflow-y: auto;
    scroll-snap-type: y mandatory;
    padding: ${ITEM_H}px 0;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .wheel::-webkit-scrollbar { display: none; }
  .item {
    height: ${ITEM_H}px;
    width: 100%;
    scroll-snap-align: center;
    display: flex;
    align-items: center;
    gap: 12px;
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.textMuted};
    padding: 0 14px;
    opacity: 0.45;
    transition: opacity 0.15s;
  }
  .item.on { opacity: 1; color: ${({ theme }) => theme.colors.text}; font-weight: 800; }
  .item .ic { font-size: 1.3rem; }
  .item .lb { flex: 1; text-align: left; font-size: 0.97rem; }
  .item .pc { font-variant-numeric: tabular-nums; font-weight: 800; color: ${({ theme }) => theme.colors.accent}; }
  .hint { text-align: center; font-size: 0.7rem; color: ${({ theme }) => theme.colors.textMuted}; margin-top: 6px; }
`;

const Influence = styled.div`
  margin-top: 14px;
  .t { display: flex; align-items: center; gap: 6px; font-size: 0.95rem; }
  .t b { color: ${({ theme }) => theme.colors.text}; }
  .t .v { margin-left: auto; font-weight: 900; font-size: 1.2rem; color: ${({ theme }) => theme.colors.accent}; }
  input[type='range'] { width: 100%; margin: 8px 0 2px; accent-color: ${({ theme }) => theme.colors.accent}; }
  .ends { display: flex; justify-content: space-between; font-size: 0.68rem; color: ${({ theme }) => theme.colors.textMuted}; }
`;
